import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapMention from '@tiptap/extension-mention';
import { Node } from '@tiptap/core';
import 'tippy.js/dist/tippy.css';
import {
  Box,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
// Legacy DataReferenceTree - kept for AdminDataReferenceTree mode
import AdminDataReferenceTree from './AdminDataReferenceTree';
// New TypeScript DataReferencePickerDialog with CompositeDataPicker
import { DataReferencePickerDialog } from '@modules/ui-components';
import { formatReferenceForDisplay } from '../../utils/mentionUtils';
import Placeholder from '@tiptap/extension-placeholder';


// Styling for mentions (applied to NodeViewWrapper or its inner elements)
const mentionChipStyle = {
  backgroundColor: '#E8F0FE',
  color: '#1976d2',
  padding: '3px 8px',
  borderRadius: '16px',
  margin: '0 2px',
  display: 'inline-flex',
  alignItems: 'center',
  fontWeight: '500',
  cursor: 'default',
  position: 'relative',
  userSelect: 'none',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  fontSize: '0.9em',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  maxWidth: 'calc(100% - 4px)',
  whiteSpace: 'normal',
};

const mentionCloseButtonStyle = {
  display: 'inline-flex',
  marginLeft: '8px', // Increased spacing
  cursor: 'pointer',
  color: '#1976d2',
  opacity: 0.7,
  '&:hover': {
    opacity: 1,
  }
};

const MentionChipComponent = memo((props) => {
  const { node } = props;
  const { id, label } = node.attrs;

  return (
    <NodeViewWrapper
      as="span"
      className="mention-chip-wrapper"
      contentEditable={false}
      draggable={true}
      style={{
        display: 'inline-block',
        margin: '0 2px',
        verticalAlign: 'baseline',
      }}
    >
      <span
        className="mention-chip"
        data-mention-id={id}
        style={mentionChipStyle}
      >
        {label}
      </span>
    </NodeViewWrapper>
  );
});

const MentionChipNode = Node.create({
  name: 'mentionChip',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-mention-id'),
        renderHTML: attributes => ({ 'data-mention-id': attributes.id }),
      },
      label: {
        default: '',
        parseHTML: element => element.getAttribute('data-label') || element.textContent,
        renderHTML: attributes => ({ 'data-label': attributes.label }),
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', { ...HTMLAttributes, class: 'rendered-mention-chip-html' }, HTMLAttributes.label || HTMLAttributes.id || ''];
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-mention-id]',
        getAttrs: domNode => {
          const dom = domNode;
          return {
            id: dom.getAttribute('data-mention-id'),
            label: dom.getAttribute('data-label') || dom.textContent,
          };
        },
      },
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MentionChipComponent);
  },
  
  renderText({ node }) {
    return node.attrs.id;
  },
});

function RichMentionEditor({ value, onChange, currentProjectId, currentUser, audiences, actorsByAudience, placeholder, tasks, studyCases, project, adminLibraryMode, readOnly = false }) {
  const [openTreeDialog, setOpenTreeDialog] = useState(false);
  const [tempTreeSelection, setTempTreeSelection] = useState([]);
  const [initialTempTreeSelection, setInitialTempTreeSelection] = useState([]);

  const getFormattedLabel = useCallback((refString) => {
    return formatReferenceForDisplay(refString, audiences, actorsByAudience, tasks, studyCases);
  }, [audiences, actorsByAudience, tasks, studyCases]);

  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      StarterKit.configure({}),
      MentionChipNode,
      TiptapMention.configure({
        HTMLAttributes: {
          class: 'suggestion-mention-trigger',
        },
        suggestion: {
          char: '@',
          items: ({ query }) => {
             return query.length > 0 ? [{ id: 'trigger-dialog', label: `Search for "${query}"...` }] : [{id: 'open-dialog', label: 'Open data selector...'}] ;
          },
          command: (cmdArgs) => {
            if (!cmdArgs) {
              console.error('[TIPTAP_DEBUG] Mention command called with undefined arguments.');
              if (!openTreeDialog) {
                handleOpenTreeDialog();
              }
              return;
            }
            const { editor: currentEditor, range, props: itemProps } = cmdArgs;

            if (!itemProps) {
              console.error('[TIPTAP_DEBUG] Mention command called with undefined itemProps (props property within cmdArgs).');
              if (currentEditor && range) {
                currentEditor.chain().focus().deleteRange(range).run();
              }
              if (!openTreeDialog) {
                handleOpenTreeDialog();
              }
              return;
            }
            
            if (itemProps.id === 'trigger-dialog' || itemProps.id === 'open-dialog') {
                currentEditor.chain().focus().deleteRange(range).run();
                if (!openTreeDialog) {
                  handleOpenTreeDialog(); 
                }
                return;
            }

            currentEditor
              .chain()
              .focus()
              .insertContentAt(range, [
                {
                  type: MentionChipNode.name, 
                  attrs: {
                    id: itemProps.id,
                    label: getFormattedLabel(itemProps.id)
                  },
                },
                { type: 'text', text: ' ' }, 
              ])
              .run();
            currentEditor.commands.setTextSelection(currentEditor.state.doc.content.size); 
          },
          render: () => {
            return {
              onStart: props => {
                if (!openTreeDialog) {
                  handleOpenTreeDialog();
                }
              },
              onUpdate(props) {
                 if (props.query === '' && openTreeDialog && !dialogJustClosedRef.current) {
                 }
              },
              onKeyDown(props) {
                if (props.event.key === 'Escape') {
                  if (openTreeDialog) {
                     handleCloseTreeDialog();
                     return true; 
                  }
                }
                return false; 
              },
              onExit() { },
            };
          },
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Type instructions and use @ to insert references to audience, actors, tasks, etc.',
        showOnlyWhenEditable: true,
        showOnlyCurrent: false,
      }),
    ],
    content: value,
    onUpdate: ({ editor: currentEditor }) => {
      if (!readOnly) {
        const html = currentEditor.getHTML();
        onChange(html);
      }

      const currentContentText = currentEditor.getText();
      if (dialogJustClosedRef.current && !currentContentText.endsWith('@')) {
        dialogJustClosedRef.current = false;
      }
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor-styles',
      },
      handleKeyDown: (view, event) => {
        if (event.key === 'Backspace') {
          const { selection } = view.state;
          if (selection.empty) {
            const $cursorPos = selection.$from;
            const nodeBefore = $cursorPos.nodeBefore;
            if (nodeBefore && nodeBefore.type.name === MentionChipNode.name) {
              const from = $cursorPos.pos - nodeBefore.nodeSize;
              const to = $cursorPos.pos;
              view.dispatch(view.state.tr.delete(from, to));
              return true;
            }
          }
        }
        if (event.key === 'Delete') {
          const { selection } = view.state;
          if (selection.empty) {
            const $cursorPos = selection.$from;
            const nodeAfter = $cursorPos.nodeAfter;
            if (nodeAfter && nodeAfter.type.name === MentionChipNode.name) {
              const from = $cursorPos.pos;
              const to = $cursorPos.pos + nodeAfter.nodeSize;
              view.dispatch(view.state.tr.delete(from, to));
              return true;
            }
          }
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  const dialogJustClosedRef = useRef(false);

  const handleOpenTreeDialog = () => {
    if (openTreeDialog) return;
    dialogJustClosedRef.current = false;
    setTempTreeSelection([]); // Always start with empty selection
    setOpenTreeDialog(true);
  };

  const handleCloseTreeDialog = useCallback(() => {
    setOpenTreeDialog(false);
    dialogJustClosedRef.current = true;
    
    if (editor && editor.getText().endsWith('@') && editor.isFocused) {
      const currentText = editor.getText();
      const transaction = editor.state.tr.delete(currentText.length - 1, currentText.length);
      editor.view.dispatch(transaction);
    }
    editor?.chain().focus().run();
  }, [editor]);


  /**
   * Handle selection from new DataReferencePickerDialog
   * Receives { paths, hydratedData } from the dialog
   */
  const handleNewPickerApply = useCallback((result) => {
    const { paths } = result;
    // hydratedData is available in result.hydratedData if needed for AI context

    if (!editor || !paths || paths.length === 0) {
      return;
    }

    let transaction = editor.state.tr;
    let insertPos = editor.state.selection.to;

    // Remove trailing @ if present
    if (editor.state.doc.textBetween(insertPos - 1, insertPos, "\0", "\0") === '@') {
      transaction = transaction.delete(insertPos - 1, insertPos);
      insertPos -= 1;
    }

    paths.forEach((refString, idx) => {
      const mentionNodeJSON = {
        type: MentionChipNode.name,
        attrs: { id: refString, label: getFormattedLabel(refString) },
      };
      const nodeToInsert = editor.schema.nodeFromJSON(mentionNodeJSON);
      transaction = transaction.insert(insertPos, nodeToInsert);
      insertPos += nodeToInsert.nodeSize;
      if (idx < paths.length - 1) {
        transaction = transaction.insertText(', ', insertPos);
        insertPos += 2;
      } else {
        transaction = transaction.insertText(' ', insertPos);
        insertPos += 1;
      }
    });

    editor.view.dispatch(transaction);
    editor.commands.setTextSelection(insertPos);

    dialogJustClosedRef.current = true;
    setOpenTreeDialog(false);
    editor?.chain().focus().run();
  }, [editor, getFormattedLabel]);

  /**
   * Legacy handler for AdminDataReferenceTree (admin mode)
   */
  const handleApplyTreeSelection = () => {
    if (!editor || !tempTreeSelection || tempTreeSelection.length === 0) {
      handleCloseTreeDialog();
      return;
    }

    let transaction = editor.state.tr;
    let insertPos = editor.state.selection.to;

    if (editor.state.doc.textBetween(insertPos -1, insertPos, "\0", "\0") === '@') {
      transaction = transaction.delete(insertPos -1, insertPos);
      insertPos -=1;
    }

    tempTreeSelection.forEach((refString, idx) => {
      const mentionNodeJSON = {
        type: MentionChipNode.name,
        attrs: { id: refString, label: getFormattedLabel(refString) },
      };
      const nodeToInsert = editor.schema.nodeFromJSON(mentionNodeJSON);
      transaction = transaction.insert(insertPos, nodeToInsert);
      insertPos += nodeToInsert.nodeSize;
      if (idx < tempTreeSelection.length - 1) {
        transaction = transaction.insertText(', ', insertPos);
        insertPos += 2;
      } else {
        transaction = transaction.insertText(' ', insertPos);
        insertPos += 1;
      }
    });

    editor.view.dispatch(transaction);
    editor.commands.setTextSelection(insertPos);
    setTempTreeSelection([]);
    handleCloseTreeDialog();
  };

  const handleTreeSelectionChange = (newSelection) => {
    setTempTreeSelection(newSelection);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && openTreeDialog) {
        handleCloseTreeDialog();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [openTreeDialog, handleCloseTreeDialog]);


  if (!editor) {
    return <Typography>Loading editor...</Typography>;
  }

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          border: '1px solid #ced4da',
          borderRadius: '4px',
          minHeight: '120px',
          cursor: 'text',
          position: 'relative',
          maxHeight: 250,
          overflowY: 'auto',
          wordBreak: 'break-all',
          overflowWrap: 'anywhere',
          '& .ProseMirror': {
            minHeight: '100px',
            outline: 'none',
            padding: '8px',
            '& .ProseMirror-selectednode': {
              outline: '2px solid #8cf',
            }
          },
          '& .ProseMirror p.is-editor-empty:first-child::before': {
            content: 'attr(data-placeholder)',
            float: 'left',
            color: '#adb5bd',
            pointerEvents: 'none',
            height: 0,
          }
        }}
        onClick={() => editor.chain().focus().run()}
      >
        <EditorContent editor={editor} />
      </Paper>

      {adminLibraryMode ? (
        <Dialog
          open={openTreeDialog}
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              handleCloseTreeDialog();
            }
          }}
          maxWidth="sm"
          fullWidth
          slotProps={{
            backdrop: {
              sx: {
                backgroundColor: 'rgba(0, 0, 0, 0.15)',
                backdropFilter: 'blur(20px)',
              }
            }
          }}
        >
          <DialogTitle sx={{ m: 0, p: 2 }}>
            Select Data References
            <IconButton
              aria-label="close"
              onClick={handleCloseTreeDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p:2 }}>
            <AdminDataReferenceTree
              value={tempTreeSelection}
              onChange={handleTreeSelectionChange}
              onApply={handleApplyTreeSelection}
              onCancel={handleCloseTreeDialog}
            />
          </DialogContent>
          <DialogActions sx={{ px:2, py:1 }}>
            <Button
              onClick={handleApplyTreeSelection}
              variant="contained"
              color="primary"
              disabled={!tempTreeSelection || tempTreeSelection.length === 0}
            >
              Apply Selection
            </Button>
            <Button onClick={handleCloseTreeDialog} color="inherit">
              Done
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <DataReferencePickerDialog
          open={openTreeDialog}
          onClose={handleCloseTreeDialog}
          onApply={handleNewPickerApply}
          projectId={currentProjectId}
          title="Select Data References"
        />
      )}
    </Box>
  );
}

export default RichMentionEditor;